/**
 * Plan Selector web component.
 *
 * Renders a list of plan cards (one per subscribable variant). The selected
 * card drives both the variant id and the selling plan submitted to /cart/add.
 *
 * Acts as the single product/plan picker on the PDP — replaces the variant
 * picker pills for the Plan/Interval option. Each radio carries the variant
 * id and plan id; on change, both hidden form inputs are kept in sync.
 */
class PlanSelector extends HTMLElement {
    static PLAN_TOKEN = '[plan]';
    static BILLING_TOKEN = '[billing]';
    static DEFAULT_ADD_URL = '/cart/add.js';

    constructor() {
        super();
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleRadioChange = this.handleRadioChange.bind(this);
        this.handleOuterVariantChange = this.handleOuterVariantChange.bind(this);
        this.outerVariantId = null;
        this.refreshSeq = 0;
    }

    connectedCallback() {
        this.bind();
        document.addEventListener('variant:change', this.handleOuterVariantChange);
    }

    disconnectedCallback() {
        document.removeEventListener('variant:change', this.handleOuterVariantChange);
        this.unbind();
    }

    /**
     * Cached variants table for the rendered product. Each entry: { id,
     * options: string[], planId }. Read once from the embedded JSON script
     * tag. Used to resolve the correct variant when the user combines a
     * non-interval selection (e.g., Strength + DHT on a bundle) with an
     * interval card pick.
     *
     * @returns {Array<{ id: number, options: string[], planId: number | null }>}
     */
    get variantsTable() {
        if (this._variantsTable) {
            return this._variantsTable;
        }
        const node = this.querySelector('[data-rsub-variants]');
        if (!node) {
            this._variantsTable = [];
            return this._variantsTable;
        }
        try {
            const parsed = JSON.parse(node.textContent || '[]');
            this._variantsTable = Array.isArray(parsed) ? parsed : [];
        } catch (_) {
            this._variantsTable = [];
        }
        return this._variantsTable;
    }

    /** @returns {number} */
    get intervalOptionIndex() {
        const raw = Number(this.dataset.intervalOptionIndex);
        return Number.isFinite(raw) && raw >= 0 ? raw : 0;
    }

    /** @returns {string} */
    get blockId() {
        return this.dataset.blockId || '';
    }

    /** @returns {string} */
    get sectionId() {
        return this.dataset.sectionId || '';
    }

    /** @returns {HTMLFormElement | null} */
    get form() {
        return this.querySelector('[data-rsub-form]');
    }

    /** @returns {HTMLInputElement | null} */
    get variantInput() {
        return this.querySelector('[data-rsub-variant-id]');
    }

    /** @returns {HTMLInputElement | null} */
    get planInput() {
        return this.querySelector('[data-rsub-selling-plan]');
    }

    /** @returns {NodeListOf<HTMLInputElement>} */
    get radios() {
        return this.querySelectorAll('[data-rsub-radio]');
    }

    /** @returns {HTMLElement | null} */
    get subtext() {
        return this.querySelector('[data-rsub-subtext]');
    }

    /** @returns {HTMLElement | null} */
    get label() {
        return this.querySelector('[data-rsub-label]');
    }

    /** @returns {HTMLButtonElement | null} */
    get submitButton() {
        return this.querySelector('[data-rsub-submit]');
    }

    /** @returns {HTMLElement | null} */
    get errorEl() {
        return this.querySelector('[data-rsub-error]');
    }

    /** @returns {HTMLElement | null} */
    get errorTextEl() {
        return this.querySelector('[data-rsub-error-text]');
    }

    /**
     * Wire up form/radio listeners and seed the inputs from the default
     * checked radio.
     */
    bind() {
        const form = this.form;
        if (!form) {
            return;
        }

        const subtext = this.subtext;
        if (subtext && !subtext.dataset.template) {
            subtext.dataset.template = subtext.textContent || '';
        }
        const label = this.label;
        if (label && !label.dataset.template) {
            label.dataset.template = label.textContent || '';
        }

        this.radios.forEach((radio) => {
            radio.addEventListener('change', this.handleRadioChange);
            if (radio.checked) {
                this.syncRadio(radio);
            }
        });

        form.addEventListener('submit', this.handleSubmit);

        const closeBtn = this.querySelector('[data-rsub-error-close]');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.hideError());
        }
    }

    unbind() {
        this.radios.forEach((radio) => {
            radio.removeEventListener('change', this.handleRadioChange);
        });
        const form = this.form;
        if (form) {
            form.removeEventListener('submit', this.handleSubmit);
        }
    }

    /** @param {Event} event */
    handleRadioChange(event) {
        const radio = /** @type {HTMLInputElement} */ (event.currentTarget);
        if (radio.checked) {
            this.syncRadio(radio);
        }
    }

    /**
     * Sync the hidden inputs (variant id + selling plan) plus the submit
     * button copy and the per-plan extras visibility from the radio's data
     * attributes.
     *
     * On products with multiple options (e.g., the bundle PDP with Strength,
     * DHT, and Interval) we resolve the variant by combining the radio's
     * interval value with the non-interval option values from the active
     * outer variant. This keeps the user's Strength + DHT pill choices intact
     * across plan card switches.
     *
     * @param {HTMLInputElement} radio
     */
    syncRadio(radio) {
        const resolved = this.resolveVariantForRadio(radio);
        const planInput = this.planInput;
        if (planInput) {
            planInput.value = resolved.planId
                ? String(resolved.planId)
                : (radio.value || '');
        }
        const variantInput = this.variantInput;
        if (variantInput && resolved.variantId) {
            variantInput.value = String(resolved.variantId);
            this.syncSectionVariantContent(String(resolved.variantId));
        }
        const billing = radio.getAttribute('data-billing-summary') || '';
        const planTitle = radio.getAttribute('data-plan-title') || '';
        this.applyTokens(this.label, planTitle, billing);
        this.applyTokens(this.subtext, planTitle, billing);
        this.toggleExtras(radio.value);
    }

    /**
     * Keep the section's per-variant accordion content in sync with the
     * variant the plan card resolves to. Direct method call — dispatching
     * variant:change here would loop back into handleOuterVariantChange.
     * @param {string} variantId
     */
    syncSectionVariantContent(variantId) {
        const section = /** @type {any} */ (this.closest('product-info-and-media-component'));
        if (!section || typeof section.updateVariantAccordionContent !== 'function') return;
        // Skip when the id belongs to another product (block's Product setting
        // can target a different product than the page's accordions).
        if (!section.querySelector(`[data-variant-accordion-content][data-variant-id="${variantId}"]`)) return;
        section.dataset.currentVariantId = variantId;
        section.updateVariantAccordionContent(variantId);
    }

    /**
     * Resolve the variant id + selling plan id matching the radio's interval
     * value combined with the non-interval option values of the currently
     * active outer variant (the one driven by the section's variant-picker).
     *
     * Falls back to the radio's own data-variant-id / data-plan-id if the
     * lookup fails (e.g., variants table empty or no outer variant context).
     *
     * @param {HTMLInputElement} radio
     * @returns {{ variantId: number | null, planId: number | null }}
     */
    resolveVariantForRadio(radio) {
        const fallbackVariantId = Number(radio.getAttribute('data-variant-id') || 0) || null;
        const fallbackPlanId = Number(radio.getAttribute('data-plan-id') || 0) || null;
        const intervalValue = radio.getAttribute('data-interval-value') || '';
        const variants = this.variantsTable;
        if (!variants.length || !intervalValue) {
            return { variantId: fallbackVariantId, planId: fallbackPlanId };
        }

        const intervalIndex = this.intervalOptionIndex;
        const outerVariant = this.findOuterVariant(variants);
        const lockedOptions = outerVariant ? outerVariant.options : null;

        const target = variants.find((v) => {
            if (!Array.isArray(v.options) || v.options[intervalIndex] !== intervalValue) {
                return false;
            }
            if (!lockedOptions) {
                return true;
            }
            return v.options.every((opt, i) => i === intervalIndex || opt === lockedOptions[i]);
        });

        if (!target) {
            return { variantId: fallbackVariantId, planId: fallbackPlanId };
        }
        return {
            variantId: target.id || fallbackVariantId,
            planId: target.planId || fallbackPlanId,
        };
    }

    /**
     * Locate the active outer variant in the variants table. Prefers the id
     * captured from the most recent variant:change event; falls back to the
     * non-rsub form's hidden id input on the page (the variant-picker form);
     * falls back to the first variant in the table.
     *
     * @param {Array<{ id: number, options: string[] }>} variants
     */
    findOuterVariant(variants) {
        const candidateIds = [];
        if (this.outerVariantId) {
            candidateIds.push(Number(this.outerVariantId));
        }
        document.querySelectorAll('input[name="id"]').forEach((el) => {
            if (!(el instanceof HTMLInputElement)) {
                return;
            }
            if (el.hasAttribute('data-rsub-variant-id')) {
                return;
            }
            // Variant pills are radios named "id" — only the checked one
            // reflects the actual selection (unchecked ones resolved to
            // variants[0] on ?variant= deep links).
            if (el.type === 'radio' && !el.checked) {
                return;
            }
            const v = Number(el.value);
            if (v) {
                candidateIds.push(v);
            }
        });
        for (const id of candidateIds) {
            const match = variants.find((v) => v.id === id);
            if (match) {
                return match;
            }
        }
        return variants[0] || null;
    }

    /**
     * Listen to the section variant-picker's variant:change event. Strength /
     * DHT changes shift the current_variant context that drives card metaobject
     * content (each Strength × DHT combo can have its own subscription_plan_card
     * metaobject), so we re-fetch the section HTML with the new variant param
     * and swap the plan-selector innerHTML in place. The form's hidden inputs
     * are then re-bound from the freshly-rendered default radio.
     *
     * @param {Event} event
     */
    async handleOuterVariantChange(event) {
        const detail = /** @type {CustomEvent<{ variantId?: string | number }>} */ (event).detail;
        const variantId = detail && detail.variantId;
        if (variantId) {
            this.outerVariantId = Number(variantId);
        }
        if (!variantId || !this.sectionId || !this.blockId) {
            const checked = this.querySelector('[data-rsub-radio]:checked');
            if (checked instanceof HTMLInputElement) {
                this.syncRadio(checked);
            }
            return;
        }
        const seq = ++this.refreshSeq;
        const url =
            window.location.pathname +
            '?section_id=' +
            encodeURIComponent(this.sectionId) +
            '&variant=' +
            encodeURIComponent(String(variantId));

        try {
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) {
                return;
            }
            const html = await res.text();
            if (seq !== this.refreshSeq) {
                return;
            }
            const wrapper = document.createElement('div');
            wrapper.innerHTML = html;
            const fresh = wrapper.querySelector('#rsub-' + this.blockId);
            if (!fresh) {
                return;
            }
            this.unbind();
            this._variantsTable = null;
            this.innerHTML = fresh.innerHTML;
            this.bind();
        } catch (_) {
            const checked = this.querySelector('[data-rsub-radio]:checked');
            if (checked instanceof HTMLInputElement) {
                this.syncRadio(checked);
            }
        }
    }

    /** @param {string} planId */
    toggleExtras(planId) {
        const extras = this.querySelectorAll('[data-rsub-extras]');
        extras.forEach((el) => {
            if (el.getAttribute('data-plan-id') === planId) {
                el.removeAttribute('hidden');
            } else {
                el.setAttribute('hidden', '');
            }
        });
    }

    /**
     * Replace [plan] / [billing] tokens in label/subtext template with the
     * currently-selected plan's values.
     *
     * @param {HTMLElement | null} el
     * @param {string} planTitle
     * @param {string} billing
     */
    applyTokens(el, planTitle, billing) {
        if (!el) {
            return;
        }
        const template = el.dataset.template || '';
        el.textContent = template
            .split(PlanSelector.PLAN_TOKEN)
            .join(planTitle)
            .split(PlanSelector.BILLING_TOKEN)
            .join(billing);
    }

    /** @param {Event} event */
    async handleSubmit(event) {
        event.preventDefault();
        const button = this.submitButton;
        const labelEl = button ? button.querySelector('.rsub-submit__label') : null;
        const originalLabel = labelEl ? labelEl.textContent || '' : '';
        this.hideError();
        this.setLoading(true, labelEl);

        try {
            const data = await this.addToCart();
            const itemCount = await this.resolveItemCount(data);
            this.dispatchCartUpdate(data, itemCount);
            this.openCartDrawer();
            if (labelEl) {
                labelEl.textContent = originalLabel;
            }
        } catch (error) {
            console.error('[plan-selector] add to cart error', error);
            if (labelEl) {
                labelEl.textContent = originalLabel;
            }
            const message = error instanceof Error ? error.message : 'Could not add to cart.';
            this.showError(message);
        } finally {
            this.setLoading(false, null);
        }
    }

    /**
     * Render an inline error under the submit button. Auto-dismisses after
     * 6s; can be dismissed manually via the close button.
     *
     * @param {string} message
     */
    showError(message) {
        const el = this.errorEl;
        const textEl = this.errorTextEl;
        if (!el || !textEl) return;
        textEl.textContent = message;
        el.classList.remove('rsub-error--leaving');
        el.hidden = false;
        if (this._errorTimer) {
            clearTimeout(this._errorTimer);
        }
        this._errorTimer = window.setTimeout(() => this.hideError(), 6000);
    }

    hideError() {
        const el = this.errorEl;
        if (!el || el.hidden) return;
        if (this._errorTimer) {
            clearTimeout(this._errorTimer);
            this._errorTimer = null;
        }
        el.classList.add('rsub-error--leaving');
        const onEnd = () => {
            el.removeEventListener('animationend', onEnd);
            el.hidden = true;
            el.classList.remove('rsub-error--leaving');
        };
        el.addEventListener('animationend', onEnd);
    }

    /**
     * @param {boolean} isLoading
     * @param {Element | null} labelEl
     */
    setLoading(isLoading, labelEl) {
        const button = this.submitButton;
        if (button) {
            button.disabled = isLoading;
        }
        if (isLoading && labelEl) {
            labelEl.textContent = '...';
        }
    }

    /**
     * Read the kit items JSON for a given plan id. Items come from the
     * subscription_plan_card.included_items metaobject list, embedded by
     * plan-selector.liquid as one <script> tag per plan.
     *
     * @param {string} planId
     * @returns {Array<{ variant_id: number | null; quantity: number; is_free: boolean; label: string; quantity_label: string }>}
     */
    getPlanItems(planId) {
        if (!planId) return [];
        const node = this.querySelector(
            `[data-rsub-plan-items][data-plan-id="${planId}"]`,
        );
        if (!node) return [];
        try {
            const parsed = JSON.parse(node.textContent || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
            return [];
        }
    }

    /**
     * Build the multi-line cart add request. One line per kit component.
     *
     * Convention: items with variant_id == null are topical placeholders. The
     * topical variant id is read from the active outer variant's
     * topicalVariantId field (variant metafield custom.topical_variant) so the
     * cart line points to the right strength × DHT × supply SKU.
     *
     * Subscription items (is_free === false) carry the selling plan. Free
     * items (brush, roller) are added as one-time and tagged with _kit_free
     * so the kit-free-items Discount Function applies 100% off at checkout.
     *
     * All lines share a generated _kit_id property so the cart drawer can
     * group them under the kit parent.
     *
     * @returns {Array<{ id: number; quantity: number; selling_plan?: number; properties: Record<string,string> }>}
     */
    buildKitCartItems() {
        const variantInput = this.variantInput;
        const planInput = this.planInput;
        if (!variantInput) throw new Error('Variant input not found');

        const outerVariantId = Number(variantInput.value);
        const planId = planInput ? planInput.value : '';
        const planItems = this.getPlanItems(planId);

        const outerVariant = this.variantsTable.find((v) => v.id === outerVariantId);
        const topicalVariantId = outerVariant && outerVariant.topicalVariantId
            ? Number(outerVariant.topicalVariantId)
            : null;
        const topicalSellingPlanId = outerVariant && outerVariant.topicalSellingPlanId
            ? Number(outerVariant.topicalSellingPlanId)
            : null;
        const topicalQuantity = outerVariant && outerVariant.topicalQuantity
            ? Number(outerVariant.topicalQuantity)
            : 1;
        const topicalQuantityLabel = (outerVariant && outerVariant.topicalQuantityLabel) || '';
        const topicalLabel = (outerVariant && outerVariant.topicalLabel) || '';
        const topicalIsFree = !!(outerVariant && outerVariant.topicalIsFree);

        const kitId = this.generateKitId();
        const parentTitle = this.dataset.kitParent || '';
        let parentTagged = false;

        const built = [];
        // Pass the kit variant id on every component line. The Cart Transform
        // Function reads this property and emits a `linesMerge` op so the
        // cart shows a single bundle parent (`has_components: true`) with the
        // components nested underneath — same UX as Shopify's native bundles.
        const kitParentVariantGid = `gid://shopify/ProductVariant/${outerVariantId}`;

        // Topical line: always added from the kit variant's topical_variant
        // metaobject (Subscription Plan Item) when set. Independent of the
        // plan items list — the topical varies per kit variant while the
        // shared plan card only describes the components common to all
        // variants of the same plan (shampoo, brush, roller).
        if (topicalVariantId && Number(topicalVariantId) !== outerVariantId) {
            /** @type {Record<string,string>} */
            const topicalProps = {
                _kit_id: kitId,
                _kit_role: topicalLabel || 'Topical',
                _kit_topical: 'true',
                _kit_unit_qty: String(topicalQuantity || 1),
                _kit_parent_variant_id: kitParentVariantGid,
            };
            if (topicalQuantityLabel) {
                topicalProps._kit_display_qty = topicalQuantityLabel;
            }
            if (parentTitle) {
                topicalProps._kit_parent = parentTitle;
                parentTagged = true;
            }
            if (topicalIsFree) {
                topicalProps._kit_free = 'true';
            }
            /** @type {{ id: number; quantity: number; selling_plan?: number; properties: Record<string,string> }} */
            const topicalLine = {
                id: Number(topicalVariantId),
                quantity: topicalQuantity || 1,
                properties: topicalProps,
            };
            if (!topicalIsFree && topicalSellingPlanId) {
                topicalLine.selling_plan = Number(topicalSellingPlanId);
            }
            built.push(topicalLine);
        }

        for (const it of planItems) {
            // Items without variant_reference are topical placeholders — already
            // handled above from the variant metafield. Skip to avoid duplicates.
            if (it.variant_id == null) continue;
            const variantId = it.variant_id;
            const lineSellingPlanId = it.selling_plan_id;
            const lineQuantity = Number(it.quantity) || 1;
            const lineQuantityLabel = it.quantity_label || '';
            const lineLabel = it.label || '';
            const lineIsFree = !!it.is_free;

            // Defensive: never re-add the kit variant itself as a child line.
            if (Number(variantId) === outerVariantId) {
                console.warn('[plan-selector] skipping item that resolves to kit variant', {
                    variantId,
                    outerVariantId,
                    item: it,
                });
                continue;
            }
            /** @type {Record<string,string>} */
            const properties = {
                _kit_id: kitId,
                _kit_role: lineLabel,
                _kit_unit_qty: String(lineQuantity || 1),
                _kit_parent_variant_id: kitParentVariantGid,
            };
            if (lineQuantityLabel) {
                properties._kit_display_qty = lineQuantityLabel;
            }
            if (!parentTagged && parentTitle) {
                properties._kit_parent = parentTitle;
                parentTagged = true;
            }
            if (lineIsFree) {
                properties._kit_free = 'true';
            }
            /** @type {{ id: number; quantity: number; selling_plan?: number; properties: Record<string,string> }} */
            const line = {
                id: Number(variantId),
                quantity: lineQuantity,
                properties,
            };
            if (!lineIsFree && lineSellingPlanId) {
                line.selling_plan = Number(lineSellingPlanId);
            }
            built.push(line);
        }

        if (built.length === 0) {
            const fallback = { id: outerVariantId, quantity: 1, properties: {} };
            if (planId) fallback.selling_plan = Number(planId);
            built.push(fallback);
        }

        // Stamp each free line with the variant GIDs of the kit's paid members.
        // The kit-free-items Discount Function treats the kit as atomic: it only
        // applies 100% off to a free line while every required paid variant is
        // still in the cart. Removing any paid item (e.g. the Topical or Shampoo)
        // at checkout drops the free Brush/Roller discount. Lines are grouped by
        // _kit_id so mixed carts (multiple kits) list only their own paid members.
        for (const line of built) {
            const props = line.properties;
            if (!props || !props._kit_id) {
                continue;
            }
            const paidGids = built
                .filter(
                    (other) =>
                        other.properties &&
                        other.properties._kit_id === props._kit_id &&
                        other.properties._kit_free !== 'true'
                )
                .map((other) => `gid://shopify/ProductVariant/${other.id}`);
            if (props._kit_free === 'true' && paidGids.length > 0) {
                props._kit_required_variant_ids = paidGids.join(',');
            }
        }

        return built;
    }

    /** @returns {string} */
    generateKitId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'kit-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    /** @returns {Promise<any>} */
    async addToCart() {
        const form = this.form;
        if (!form) {
            throw new Error('Form not found');
        }
        const items = this.buildKitCartItems();
        const themeAddUrl =
            typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url
                ? Theme.routes.cart_add_url
                : null;
        const addUrl = themeAddUrl || PlanSelector.DEFAULT_ADD_URL;
        const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections_url: window.location.pathname }),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || 'Add to cart failed');
        }
        return response.json();
    }

    /**
     * @param {any} data
     * @returns {Promise<number>}
     */
    async resolveItemCount(data) {
        if (typeof data.item_count === 'number') {
            return data.item_count;
        }
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartRes.ok) {
            return 0;
        }
        const cart = await cartRes.json();
        return cart.item_count || 0;
    }

    /**
     * @param {any} data
     * @param {number} itemCount
     */
    dispatchCartUpdate(data, itemCount) {
        const variantInput = this.variantInput;
        const variantId = variantInput ? variantInput.value : '';
        const cartEvent = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
                resource: data,
                sourceId: 'plan-selector',
                data: {
                    source: 'plan-selector',
                    itemCount: itemCount || 0,
                    variantId: variantId,
                    sections: data.sections,
                },
            },
        });
        document.dispatchEvent(cartEvent);
    }

    openCartDrawer() {
        const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (
            document.querySelector('cart-drawer-component')
        );
        if (drawer && typeof drawer.open === 'function') {
            drawer.open();
        }
    }
}

if (!customElements.get('plan-selector')) {
    customElements.define('plan-selector', PlanSelector);
}
